import Markdown from "react-markdown"

import { ohneLatex } from "@/lib/coach-formeln"
import { remarkGfmOhneAutolinks } from "@/lib/remark-gfm-ohne-autolinks"

/**
 * Renders the coach's replies as markdown. The model is told it may use light
 * markdown (bold, italics, short lists), so plain-text rendering left the raw
 * `**` and `-` on screen. Styling is done through element overrides rather than
 * a `prose` plugin so the type scale matches the chat bubble (14px / 1.55).
 *
 * Gerechnet wird in Klartext, nicht im Formelsatz: `ohneLatex` räumt weg, was
 * ein Modell trotz Prompt-Regel an `\[ … \]` hinterlässt, und zwar auch in
 * Gesprächen, die vor der Regel entstanden sind.
 */
export function CoachMarkdown({ text }: { text: string }) {
  return (
    <div className="space-y-2 text-[14px] leading-[1.55] [&>:first-child]:mt-0 [&>:last-child]:mb-0">
      <Markdown
        remarkPlugins={[remarkGfmOhneAutolinks]}
        components={{
          p: ({ children }) => <p>{children}</p>,
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          del: ({ children }) => <del className="opacity-70">{children}</del>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-primary underline underline-offset-2"
            >
              {children}
            </a>
          ),
          ul: ({ children }) => (
            <ul className="list-disc space-y-1 pl-5">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal space-y-1 pl-5">{children}</ol>
          ),
          li: ({ children }) => <li className="pl-0.5">{children}</li>,
          h1: ({ children }) => (
            <h1 className="mt-3 mb-1 font-display text-[16px] font-semibold">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-3 mb-1 font-display text-[15px] font-semibold">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-3 mb-1 font-semibold">{children}</h3>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-[var(--rule)] pl-3 text-foreground/80 italic">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="border-[var(--rule)]" />,
          code: ({ className, children }) => {
            // Fenced blocks carry a `language-*` class; inline code has none.
            const isBlock = /language-/.test(className ?? "")
            return isBlock ? (
              <code className="block font-mono text-[13.5px]">{children}</code>
            ) : (
              <code className="rounded bg-black/5 px-1 py-0.5 font-mono text-[13.5px] dark:bg-white/10">
                {children}
              </code>
            )
          },
          pre: ({ children }) => (
            <pre className="overflow-x-auto rounded-md border border-[var(--rule)] bg-black/5 p-3 dark:bg-white/10">
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-[var(--rule)] px-2 py-1 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-[var(--rule)] px-2 py-1 align-top">
              {children}
            </td>
          ),
        }}
      >
        {ohneLatex(text)}
      </Markdown>
    </div>
  )
}
